// SCF domain icon lookup.
//
// The PNGs in ContentRegistry/assets are named like
// "Configuration Management (CFG).png" — the SCF three-letter code lives
// inside the parentheses. We statically bundle all of them via Vite's
// glob import and derive a code → URL map at module load.

const RAW_ASSETS = import.meta.glob("../../assets/*.png", {
  eager: true,
  import: "default",
  query: "?url",
}) as Record<string, string>;

export interface ScfDomain {
  code: string;
  name: string;
  iconUrl: string;
}

/** Parse ".../Full Domain Name (XYZ).png" into its name, code and icon. */
function domainFromFilename(path: string): ScfDomain | null {
  const match = path.match(/([^/\\]+) \(([^)]+)\)\.png$/i);
  if (!match) return null;
  return {
    name: match[1].trim(),
    code: match[2].trim().toUpperCase(),
    iconUrl: RAW_ASSETS[path],
  };
}

const SCF_DOMAIN_ASSETS = Object.keys(RAW_ASSETS)
  .map(domainFromFilename)
  .filter((domain): domain is ScfDomain => domain !== null);

const DOMAIN_ICON_URLS: Record<string, string> = SCF_DOMAIN_ASSETS.reduce(
  (acc, domain) => {
    acc[domain.code] = domain.iconUrl;
    return acc;
  },
  {} as Record<string, string>,
);

/** Public list of codes we have icons for (useful for tests / diagnostics). */
export const DOMAIN_ICON_CODES = Object.keys(DOMAIN_ICON_URLS);

/** Every SCF domain: the governance domain leads, the rest go alphabetical. */
export const SCF_DOMAINS: ScfDomain[] = [...SCF_DOMAIN_ASSETS].sort((a, b) =>
  a.code === "SCRG" ? -1 : b.code === "SCRG" ? 1 : a.name.localeCompare(b.name),
);

/** Returns the icon URL for an SCF domain code, or `undefined` if none exists. */
export function getDomainIconUrl(code: string | null | undefined): string | undefined {
  if (!code) return undefined;
  return DOMAIN_ICON_URLS[String(code).trim().toUpperCase()];
}
